import { useRouter } from 'next/router';
import { type CSSProperties, memo, type PointerEvent, useCallback, useEffect, useMemo, useRef } from 'react';

import { ChevronLeftIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { cn } from '@air/utils-tailwind';

import { DesktopNotifications } from '~/components/AirNotifications/DesktopNotifications';
import { Panel } from '~/components/v2/components/shared/panel/Panel';
import { PanelContent } from '~/components/v2/components/shared/panel/PanelContent';
import { PanelOverlay } from '~/components/v2/components/shared/panel/PanelOverlay';
import { MainSideNavOverlayTray } from '~/components/v2/components/workspace/main-side-nav/MainSideNavOverlayTray';
import { useWorkspaceChromeHeaderBottom } from '~/components/v2/hooks/useWorkspaceChromeHeaderBottom';
import { useWorkspaceNavPushMode } from '~/components/v2/hooks/useWorkspaceNavPushMode';
import { useWorkspaceNotificationsStore } from '~/components/v2/store/features/workspace/useWorkspaceNotificationsStore';
import { useWorkspaceSidebarPushStore } from '~/components/v2/store/features/workspace/useWorkspaceSidebarPushStore';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';

export const WorkspaceNotification = memo(() => {
  const router = useRouter();
  const open = useWorkspaceNotificationsStore((store) => store.open);
  const setOpen = useWorkspaceNotificationsStore((store) => store.setOpen);
  const clearOpen = useWorkspaceNotificationsStore((store) => store.clearOpen);
  const { isWorkspaceNavPushMode } = useWorkspaceNavPushMode();
  const { ui2026 } = useTemporaryFeatureFlags();
  const workspaceChromeHeaderBottom = useWorkspaceChromeHeaderBottom(ui2026 && !isWorkspaceNavPushMode);
  const hasClosedOnHoverRef = useRef<boolean>();
  const panelContentRef = useRef<HTMLDivElement>(null);
  const handleClose = useCallback(() => setOpen(false), [setOpen]);

  const openPush = useWorkspaceSidebarPushStore((s) => s.openPush);
  const closePushAction = useWorkspaceSidebarPushStore((s) => s.closePush);

  const handleBackButtonClick = useCallback(() => {
    setOpen(false);
    openPush();
  }, [setOpen, openPush]);

  useEffect(() => {
    router.events.on('routeChangeComplete', clearOpen);

    return () => {
      router.events.off('routeChangeComplete', clearOpen);
    };
  }, [router.events, clearOpen]);

  useEffect(() => {
    if (open) {
      hasClosedOnHoverRef.current = false;
    }
  }, [open]);

  const handleOverlayHover = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (!open || hasClosedOnHoverRef.current) {
        return;
      }

      const panelRect = panelContentRef.current?.getBoundingClientRect();

      if (panelRect && event.clientX < panelRect.left) {
        return;
      }

      hasClosedOnHoverRef.current = true;
      setOpen(false);

      if (isWorkspaceNavPushMode) {
        closePushAction();
      }
    },
    [isWorkspaceNavPushMode, open, setOpen, closePushAction],
  );

  const panelContentStyle = useMemo((): CSSProperties | undefined => {
    if (isWorkspaceNavPushMode || workspaceChromeHeaderBottom === null) {
      return undefined;
    }

    return { top: workspaceChromeHeaderBottom };
  }, [isWorkspaceNavPushMode, workspaceChromeHeaderBottom]);

  const notifications = useMemo(
    () => (
      <DesktopNotifications
        headerLeading={
          isWorkspaceNavPushMode ? (
            <IconButton
              adsIcon
              appearance="ghost"
              color="gray"
              icon={ChevronLeftIcon}
              label="Back to menu"
              onClick={handleBackButtonClick}
            />
          ) : undefined
        }
        onClose={handleClose}
        isUI2
      />
    ),
    [handleBackButtonClick, handleClose, isWorkspaceNavPushMode],
  );

  if (ui2026 && !isWorkspaceNavPushMode) {
    return (
      <MainSideNavOverlayTray
        contentRef={panelContentRef}
        contentStyle={panelContentStyle}
        contentTestId="WORKSPACE_NOTIFICATIONS_PANEL"
        onOpenChange={setOpen}
        open={open}
        overlayTestId="WORKSPACE_NOTIFICATIONS_OVERLAY"
        placement="top"
      >
        {notifications}
      </MainSideNavOverlayTray>
    );
  }

  return (
    <Panel onOpenChange={setOpen} open={open}>
      <PanelOverlay
        className="radix-state-closed:animation-duration-300 radix-state-closed:fade-out radix-state-open:animation-duration-300 radix-state-open:fade-in fixed inset-0 z-30 radix-state-closed:animate-out radix-state-open:animate-in bg-scrim backdrop-blur-[2px]"
        data-testid="WORKSPACE_NOTIFICATIONS_OVERLAY"
        onPointerEnter={isWorkspaceNavPushMode ? undefined : handleOverlayHover}
      />
      <PanelContent
        className={cn(
          'radix-state-closed:animation-duration-300 radix-state-closed:fade-out radix-state-open:animation-duration-300 radix-state-open:fade-in motion-safe:radix-state-closed:slide-out-to-left-12 motion-safe:radix-state-open:slide-in-from-left-12 fixed radix-state-closed:animate-out radix-state-open:animate-in overflow-hidden ads:bg-base bg-gray-1 shadow-[2px_0px_8px_0px_rgba(0,0,0,0.08),_8px_0px_24px_0px_rgba(0,0,0,0.08)]',
          isWorkspaceNavPushMode
            ? 'inset-y-0 left-[var(--sidebar-width)] z-[51] w-[416px] rounded-r-8'
            : // Stay `fixed` (not `absolute`): the layout row is overflow-hidden, so absolute traps the popover under the asset grid/scrim.
              'inset-y-2 left-[calc(var(--sidebar-width)+8px)] z-40 w-[416px] rounded-8',
        )}
        data-testid="WORKSPACE_NOTIFICATIONS_PANEL"
        ref={panelContentRef}
        style={panelContentStyle}
      >
        {notifications}
      </PanelContent>
    </Panel>
  );
});

WorkspaceNotification.displayName = 'WorkspaceNotification';
